import type { CSSProperties, ReactNode } from 'react';
import type { FocusPort } from '@player/ports';
import { colors, performanceCssVariables, type PerformanceSettings } from '../theme.js';
import { SideMenu, type MenuEntry } from './SideMenu.js';

/**
 * La cornice dell'app: la colonna del menu **accanto** alla schermata, non dentro.
 *
 * ## Perché esiste
 *
 * Nel prototipo la colonna e la schermata sono **fratelli** dentro un `display:flex` — `showRail` è
 * un interruttore del contenitore, e ogni schermata è solo il riquadro accanto. Io l'avevo scritta
 * come figlia della diretta, e la conseguenza non era estetica: **preferiti e impostazioni non
 * avevano il menu affatto.** Ci si arrivava dal menu e ci si ritrovava senza, con il solo tasto
 * Indietro per uscire — su una schermata che si raggiunge col telecomando, è un vicolo cieco.
 *
 * Tenendolo qui la colonna esiste in **un** posto: le tre schermate non possono più divergere su
 * quanto è larga, dove sta il profilo o quali voci ci sono.
 *
 * ## Visibile e a fuoco restano due cose diverse
 *
 * `menuVisible` disegna la colonna, `menuFocused` le dà i tasti. Confonderle costa caro: con la
 * guida aperta il menu si vede ma le frecce sono della griglia, e se si iscrivesse comunque al
 * telecomando la stessa freccia arriverebbe a entrambi.
 */

export interface ShellProps {
  readonly focusPort: FocusPort;
  readonly entries: ReadonlyArray<MenuEntry>;
  /** Quale sezione si sta guardando: resta accesa anche entrando nei suoi sottolivelli. */
  readonly activeId: string | null;
  /** La colonna si disegna. */
  readonly menuVisible: boolean;
  /** I tasti sono suoi. */
  readonly menuFocused: boolean;
  readonly onChoose: (id: string) => void;
  readonly onDismiss: () => void;
  readonly onSignOut: () => void;
  /** Rilegge il catalogo saltando l'archivio locale: il tasto sta in fondo alla colonna. */
  readonly onRefresh: () => void;
  readonly refreshing?: boolean;
  /** Il nome del profilo, o `null` quando non se ne conosce uno: vedi `SideMenuProps`. */
  readonly profileName?: string | null;
  /**
   * Le impostazioni prestazioni, che qui diventano **variabili CSS**.
   *
   * `performanceCssVariables` esisteva, era provata, e **nessuno la applicava**: `var(--tr)` e
   * `var(--fs)` cadevano sempre sul valore predefinito, quindi gli interruttori «Animazioni» ed
   * «Effetti visivi» non spegnevano niente. Una voce di impostazioni che non fa niente insegna che
   * le impostazioni non servono (07-PRESTAZIONI §5).
   *
   * Vanno messe **qui** perché le proprietà personalizzate si ereditano: scritte sulla cornice
   * valgono per ogni stile in linea di ogni schermata dentro, senza che nessuna debba saperlo.
   */
  readonly performance?: PerformanceSettings;
  readonly children: ReactNode;
}

export function Shell({
  focusPort,
  entries,
  activeId,
  menuVisible,
  menuFocused,
  onChoose,
  onDismiss,
  onSignOut,
  onRefresh,
  refreshing = false,
  profileName = null,
  performance = { animations: true, visualEffects: true },
  children,
}: ShellProps): ReactNode {
  return (
    <div
      data-shell="1"
      style={{ ...frameStyle, ...performanceCssVariables(performance) } as CSSProperties}
    >
      {menuVisible && (
        <SideMenu
          focusPort={focusPort}
          entries={entries}
          activeId={activeId}
          focused={menuFocused}
          onChoose={onChoose}
          onDismiss={onDismiss}
          onSignOut={onSignOut}
          onRefresh={onRefresh}
          refreshing={refreshing}
          profileName={profileName}
        />
      )}

      {/*
        Il riquadro della schermata.

        `position: relative` qui dentro vuol dire che un `inset: 0` di una sovrapposizione è **il
        riquadro accanto al menu**, e nessuno deve ricordarsi di sottrarre 280 px — che è esattamente
        il modo in cui la colonna dei canali della guida era finita sotto le voci del menu.
      */}
      <div style={{ flex: 1, minWidth: 0, position: 'relative', overflow: 'hidden', display: 'flex' }}>
        {children}
      </div>
    </div>
  );
}

const frameStyle = {
  display: 'flex',
  width: '100%',
  height: '100%',
  background: colors.background,
  color: colors.text,
  overflow: 'hidden' as const,
};
